import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  CopyCheck,
  FileWarning,
  Layers,
  Loader2,
  Upload,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FileUploader } from '@/components/import/file-uploader';
import { parseSalesCSV, type SalesCsvParseResult } from '@/lib/sales-csv-parser';
import { cn } from '@/lib/utils';
import {
  createReport,
  getReport,
  listReports,
  TEMPLATE_LABELS,
  ALL_BRAND,
  ALL_BRAND_LABEL,
} from '@/lib/api';
import type {
  AvailablePeriods,
  BrandStat,
  ReportListItem,
  ReportTemplateType,
  SalesImportRow,
} from '@/lib/api';

type PeriodType = 'month' | 'quarter' | 'year' | 'range';

const PERIOD_TYPE_LABELS: Record<PeriodType, string> = {
  month: 'Tháng',
  quarter: 'Quý',
  year: 'Năm',
  range: 'Range',
};

interface PeriodResolved {
  start: string;
  end: string;
  label: string;
}

function formatMonthLabel(month: string): string {
  const [y, m] = month.split('-');
  return `T${parseInt(m!, 10)}/${y!.slice(2)}`;
}

function formatQuarterLabel(q: string): string {
  const [y, n] = q.split('-Q');
  return `Q${n} ${y}`;
}

function quarterToRange(q: string): { start: string; end: string } | null {
  const m = q.match(/^(\d{4})-Q([1-4])$/);
  if (!m) return null;
  const year = m[1]!;
  const num = parseInt(m[2]!, 10);
  const startMonth = (num - 1) * 3 + 1;
  return {
    start: `${year}-${String(startMonth).padStart(2, '0')}`,
    end: `${year}-${String(startMonth + 2).padStart(2, '0')}`,
  };
}

function periodLabelFromRange(start: string, end: string): string {
  if (start === end) return formatMonthLabel(start);
  const [ys, ms] = start.split('-');
  const [ye, me] = end.split('-');
  if (ys === ye) {
    const sm = parseInt(ms!, 10);
    const em = parseInt(me!, 10);
    if (sm === 1 && em === 12) return `Năm ${ys}`;
    const q = Math.ceil(sm / 3);
    if (sm === (q - 1) * 3 + 1 && em === q * 3) return `Q${q} ${ys}`;
  }
  return `${ms}/${ys} → ${me}/${ye}`;
}

function suggestTitle(brand: string, periodLabel: string): string {
  if (!brand || !periodLabel) return '';
  const brandPart = brand === ALL_BRAND ? 'Monitor Category' : brand;
  return `${periodLabel} ${brandPart} Performance Review`;
}

/**
 * Detect period type from raw start/end YYYY-MM strings.
 * Used when cloning / pre-filling form from an existing report's period.
 */
function detectPeriodType(start: string, end: string): {
  type: PeriodType;
  monthValue?: string;
  quarterValue?: string;
  yearValue?: string;
} {
  if (start === end) return { type: 'month', monthValue: start };
  const [sy, sm] = start.split('-').map(Number);
  const [ey, em] = end.split('-').map(Number);
  if (sy === ey && sm && em) {
    if (sm === 1 && em === 12) return { type: 'year', yearValue: String(sy) };
    const q = Math.ceil(sm / 3);
    if (sm === (q - 1) * 3 + 1 && em === q * 3) {
      return { type: 'quarter', quarterValue: `${sy}-Q${q}` };
    }
  }
  return { type: 'range' };
}

/**
 * Compute the period right after the given one, preserving length.
 *  Q1 2026 → Q2 2026 · T12/2025 → T01/2026 · 2025 → 2026 · 2-month range → next 2 months.
 *  Used for "Tạo báo cáo tương tự" auto-suggest.
 */
function bumpPeriod(start: string, end: string): { start: string; end: string } {
  const [sy, sm] = start.split('-').map(Number);
  const [ey, em] = end.split('-').map(Number);
  if (!sy || !sm || !ey || !em) return { start, end };
  const monthsInPeriod = (ey - sy) * 12 + (em - sm) + 1;
  // Total months counter: year*12 + (month-1) gives 0-indexed month count.
  const nextStartTotal = ey * 12 + em; // = ey*12 + (em-1) + 1 = month after current end
  const nextEndTotal = nextStartTotal + (monthsInPeriod - 1);
  const fmt = (total: number) => {
    const y = Math.floor(total / 12);
    const m = (total % 12) + 1;
    return `${y}-${String(m).padStart(2, '0')}`;
  };
  return { start: fmt(nextStartTotal), end: fmt(nextEndTotal) };
}

export interface ReportCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brands: BrandStat[];
  available: AvailablePeriods | null;
  onCreated: () => void;
  /** When provided, pre-fill modal from this report (clone path from detail page).
   *  Period auto-bumps to next period of equal length. */
  cloneFrom?: {
    id: string;
    title: string;
    brand: string;
    period_start: string;
    period_end: string;
    template_type: ReportTemplateType;
    intro_text: string | null;
  };
}

export function ReportCreateModal({
  open,
  onOpenChange,
  brands,
  available,
  onCreated,
  cloneFrom,
}: ReportCreateModalProps) {
  // Form state
  const [brand, setBrand] = useState('');
  const [periodType, setPeriodType] = useState<PeriodType>('quarter');
  const [monthValue, setMonthValue] = useState('');
  const [quarterValue, setQuarterValue] = useState('');
  const [yearValue, setYearValue] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [templateType, setTemplateType] = useState<ReportTemplateType>('partner');
  const [introText, setIntroText] = useState('');

  // Clone source — when not pre-filled via cloneFrom prop, user can pick from dropdown.
  const [cloneSourceId, setCloneSourceId] = useState<string>('');
  const [recentReports, setRecentReports] = useState<ReportListItem[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);

  // CSV (now in collapse "Nâng cao")
  const [csvParseResult, setCsvParseResult] = useState<SalesCsvParseResult | null>(null);
  const [parsing, setParsing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Apply cloneFrom prop on first open. Auto-bump period to next.
  useEffect(() => {
    if (!open) return;
    if (!cloneFrom) return;
    const next = bumpPeriod(cloneFrom.period_start, cloneFrom.period_end);
    const detected = detectPeriodType(next.start, next.end);
    setBrand(cloneFrom.brand);
    setPeriodType(detected.type);
    setMonthValue(detected.monthValue ?? '');
    setQuarterValue(detected.quarterValue ?? '');
    setYearValue(detected.yearValue ?? '');
    setRangeStart(detected.type === 'range' ? next.start : '');
    setRangeEnd(detected.type === 'range' ? next.end : '');
    setTemplateType(cloneFrom.template_type);
    setIntroText(cloneFrom.intro_text ?? '');
    setCloneSourceId(cloneFrom.id);
    setTitleTouched(false); // let auto-suggest run
    setError(null);
  }, [open, cloneFrom]);

  // Fetch recent reports for clone-from dropdown (lazy on first open)
  useEffect(() => {
    if (!open) return;
    if (recentReports.length > 0) return;
    setRecentLoading(true);
    listReports({ limit: 30 })
      .then((res) => setRecentReports(res.data))
      .catch(() => {/* silent — dropdown just empty */})
      .finally(() => setRecentLoading(false));
  }, [open, recentReports.length]);

  // Reset state when modal closes
  useEffect(() => {
    if (open) return;
    setBrand('');
    setPeriodType('quarter');
    setMonthValue('');
    setQuarterValue('');
    setYearValue('');
    setRangeStart('');
    setRangeEnd('');
    setTitle('');
    setTitleTouched(false);
    setTemplateType('partner');
    setIntroText('');
    setCloneSourceId('');
    setCsvParseResult(null);
    setParsing(false);
    setShowAdvanced(false);
    setSubmitting(false);
    setError(null);
  }, [open]);

  // Resolve current period
  const periodResolved = useMemo<PeriodResolved | null>(() => {
    if (periodType === 'month' && monthValue) {
      return { start: monthValue, end: monthValue, label: formatMonthLabel(monthValue) };
    }
    if (periodType === 'quarter' && quarterValue) {
      const r = quarterToRange(quarterValue);
      if (!r) return null;
      return { start: r.start, end: r.end, label: formatQuarterLabel(quarterValue) };
    }
    if (periodType === 'year' && yearValue) {
      return { start: `${yearValue}-01`, end: `${yearValue}-12`, label: `Năm ${yearValue}` };
    }
    if (periodType === 'range' && rangeStart && rangeEnd) {
      if (rangeStart > rangeEnd) return null;
      return {
        start: rangeStart,
        end: rangeEnd,
        label: periodLabelFromRange(rangeStart, rangeEnd),
      };
    }
    return null;
  }, [periodType, monthValue, quarterValue, yearValue, rangeStart, rangeEnd]);

  // Distinct years (for Year picker)
  const availableYears = useMemo(() => {
    const set = new Set<string>();
    (available?.months ?? []).forEach((m) => {
      const [y] = m.split('-');
      if (y) set.add(y);
    });
    return Array.from(set).sort();
  }, [available]);

  // Auto-suggest title
  useEffect(() => {
    if (titleTouched) return;
    setTitle(suggestTitle(brand, periodResolved?.label ?? ''));
  }, [brand, periodResolved?.label, titleTouched]);

  // Handle clone-from dropdown pick (when cloneFrom prop NOT set)
  const handleCloneSelect = async (id: string) => {
    setCloneSourceId(id);
    if (!id) return;
    try {
      const full = await getReport(id);
      const next = bumpPeriod(full.period_start, full.period_end);
      const detected = detectPeriodType(next.start, next.end);
      setBrand(full.brand);
      setPeriodType(detected.type);
      setMonthValue(detected.monthValue ?? '');
      setQuarterValue(detected.quarterValue ?? '');
      setYearValue(detected.yearValue ?? '');
      setRangeStart(detected.type === 'range' ? next.start : '');
      setRangeEnd(detected.type === 'range' ? next.end : '');
      setTemplateType(full.template_type);
      setIntroText(full.intro_text ?? '');
      setTitleTouched(false);
    } catch (err) {
      setError(`Không tải được báo cáo gốc: ${(err as Error).message}`);
    }
  };

  const handleFileSelected = async (file: File) => {
    setError(null);
    setCsvParseResult(null);
    setParsing(true);
    try {
      setCsvParseResult(await parseSalesCSV(file));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setParsing(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    if (!brand) return setError('Vui lòng chọn hãng');
    if (!periodResolved) return setError('Vui lòng chọn period hợp lệ');
    if (!title.trim()) return setError('Vui lòng nhập tiêu đề');

    const salesRows: SalesImportRow[] | undefined =
      csvParseResult && csvParseResult.rows.length > 0 ? csvParseResult.rows : undefined;

    setSubmitting(true);
    try {
      await createReport({
        brand,
        period_start: periodResolved.start,
        period_end: periodResolved.end,
        title: title.trim(),
        template_type: templateType,
        intro_text: introText.trim() || undefined,
        sales_rows: salesRows,
      });
      onCreated();
      onOpenChange(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const csvHasErrors = csvParseResult && csvParseResult.errors.length > 0;
  const isCloning = Boolean(cloneFrom) || Boolean(cloneSourceId);
  const cloneSourceLabel = useMemo(() => {
    if (cloneFrom) return cloneFrom.title;
    const r = recentReports.find((x) => x.id === cloneSourceId);
    return r?.title ?? '';
  }, [cloneFrom, cloneSourceId, recentReports]);

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{cloneFrom ? 'Tạo báo cáo tương tự' : 'Tạo báo cáo mới'}</DialogTitle>
          <DialogDescription>
            {cloneFrom
              ? `Clone template + intro từ "${cloneFrom.title}". Period đã auto-bump sang kỳ kế tiếp — chỉnh nếu cần.`
              : 'Tạo báo cáo gắn với 1 hãng × period. Snapshot data sẽ frozen tại thời điểm tạo.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Clone source — only shown when NOT pre-filled via cloneFrom prop */}
          {!cloneFrom && (
            <div className="rounded-md border bg-muted/30 p-3 space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <CopyCheck className="h-3.5 w-3.5" />
                Clone từ báo cáo cũ (tuỳ chọn)
              </label>
              <Select value={cloneSourceId || '__none__'} onValueChange={(v) => handleCloneSelect(v === '__none__' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder={recentLoading ? 'Đang tải...' : 'Chọn báo cáo để copy template + intro...'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Tạo từ đầu (không clone)</SelectItem>
                  {recentReports.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      <span className="truncate">{r.title}</span>
                      <span className="ml-1 text-muted-foreground">· {r.brand}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Pre-fill brand · template · intro · period (auto-bump kỳ kế). Vẫn chỉnh được sau.
              </p>
            </div>
          )}

          {/* Cloned-from banner — shown when cloneFrom prop set */}
          {cloneFrom && (
            <div className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs">
              <CopyCheck className="h-4 w-4 shrink-0 text-primary" />
              <span>
                Đang clone từ:{' '}
                <span className="font-semibold">{cloneSourceLabel}</span>
              </span>
            </div>
          )}

          {/* Brand */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Hãng <span className="text-destructive">*</span>
            </label>
            <Select value={brand} onValueChange={setBrand}>
              <SelectTrigger>
                <SelectValue placeholder="Chọn hãng..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_BRAND}>
                  <span className="flex items-center gap-1.5">
                    <Layers className="h-3.5 w-3.5 text-primary" />
                    <span className="font-medium">{ALL_BRAND_LABEL}</span>
                    <span className="text-muted-foreground">— gộp tất cả hãng</span>
                  </span>
                </SelectItem>
                {brands.map((b) => (
                  <SelectItem key={b.brand} value={b.brand}>
                    {b.brand} <span className="ml-1 text-muted-foreground">({b.count} SKU)</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Period */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Period <span className="text-destructive">*</span>
            </label>
            <div className="flex gap-1.5">
              {(['month', 'quarter', 'year', 'range'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setPeriodType(t)}
                  className={cn(
                    'flex-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                    periodType === t
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background text-muted-foreground hover:bg-accent'
                  )}
                >
                  {PERIOD_TYPE_LABELS[t]}
                </button>
              ))}
            </div>

            {periodType === 'month' && (
              <Select value={monthValue} onValueChange={setMonthValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Chọn tháng..." />
                </SelectTrigger>
                <SelectContent>
                  {(available?.months ?? []).map((m) => (
                    <SelectItem key={m} value={m}>{formatMonthLabel(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {periodType === 'quarter' && (
              <Select value={quarterValue} onValueChange={setQuarterValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Chọn quý..." />
                </SelectTrigger>
                <SelectContent>
                  {(available?.quarters ?? []).map((q) => (
                    <SelectItem key={q} value={q}>{formatQuarterLabel(q)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {periodType === 'year' && (
              <Select value={yearValue} onValueChange={setYearValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Chọn năm..." />
                </SelectTrigger>
                <SelectContent>
                  {availableYears.map((y) => (
                    <SelectItem key={y} value={y}>Năm {y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {periodType === 'range' && (
              <div className="grid grid-cols-2 gap-2">
                <Select value={rangeStart} onValueChange={setRangeStart}>
                  <SelectTrigger><SelectValue placeholder="Từ tháng..." /></SelectTrigger>
                  <SelectContent>
                    {(available?.months ?? []).map((m) => (
                      <SelectItem key={m} value={m}>{formatMonthLabel(m)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={rangeEnd} onValueChange={setRangeEnd}>
                  <SelectTrigger><SelectValue placeholder="Đến tháng..." /></SelectTrigger>
                  <SelectContent>
                    {(available?.months ?? []).map((m) => (
                      <SelectItem key={m} value={m}>{formatMonthLabel(m)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {periodResolved && (
              <p className="text-[11px] text-muted-foreground">
                {isCloning && '🔄 Auto-bump · '}
                Range: <span className="font-mono">{periodResolved.start}</span> →{' '}
                <span className="font-mono">{periodResolved.end}</span>
              </p>
            )}
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <label htmlFor="report-title" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Tiêu đề <span className="text-destructive">*</span>
            </label>
            <Input
              id="report-title"
              value={title}
              onChange={(e) => {
                setTitleTouched(true);
                setTitle(e.target.value);
              }}
              placeholder="Auto-suggest từ brand × period"
              maxLength={200}
            />
          </div>

          {/* Template */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Loại báo cáo <span className="text-destructive">*</span>
            </label>
            <Select value={templateType} onValueChange={(v) => setTemplateType(v as ReportTemplateType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(TEMPLATE_LABELS) as ReportTemplateType[]).map((key) => {
                  const t = TEMPLATE_LABELS[key];
                  const Icon = t.icon;
                  return (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-1.5">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="font-medium">{t.label}</span>
                        <span className="text-muted-foreground">— {t.description}</span>
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Intro */}
          <div className="space-y-1.5">
            <label htmlFor="report-intro" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Lời mở đầu (tùy chọn)
              <span className="ml-1 font-normal text-muted-foreground">— max 500 ký tự</span>
            </label>
            <Textarea
              id="report-intro"
              value={introText}
              onChange={(e) => setIntroText(e.target.value)}
              placeholder="vd: Quý này hãng push QD-OLED, sales tăng 32% nhờ campaign Black Friday..."
              maxLength={500}
              className="min-h-[64px]"
            />
          </div>

          {/* Advanced collapse — CSV upload */}
          <details
            className="rounded-md border bg-muted/20"
            open={showAdvanced}
            onToggle={(e) => setShowAdvanced((e.target as HTMLDetailsElement).open)}
          >
            <summary className="flex cursor-pointer items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground">
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showAdvanced && 'rotate-180')} />
              <Upload className="h-3.5 w-3.5" />
              Nâng cao — Upload CSV (tuỳ chọn)
            </summary>
            <div className="space-y-2 px-3 pb-3 pt-1">
              <p className="text-[11px] text-muted-foreground">
                Hầu hết case không cần CSV — data đã có sẵn trong DB. Chỉ upload nếu PM
                có file sales mới cần UPSERT trước khi compute snapshot.
              </p>
              <FileUploader onFileSelected={handleFileSelected} disabled={parsing || submitting} />
              {parsing && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Đang parse CSV...
                </div>
              )}
              {csvParseResult && csvParseResult.rows.length > 0 && (
                <Alert variant="success">
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertTitle>{csvParseResult.rows.length} dòng valid</AlertTitle>
                  <AlertDescription className="text-xs">
                    Sẽ UPSERT vào sales_monthly khi tạo báo cáo.
                  </AlertDescription>
                </Alert>
              )}
              {csvHasErrors && (
                <Alert variant="destructive">
                  <FileWarning className="h-4 w-4" />
                  <AlertTitle>CSV có {csvParseResult.errors.length} lỗi</AlertTitle>
                  <AlertDescription className="text-xs">
                    <ul className="ml-4 list-disc">
                      {csvParseResult.errors.slice(0, 3).map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </details>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Hủy
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!brand || !periodResolved || !title.trim() || submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Tạo báo cáo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
